import React, { useMemo, useState } from 'react';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';

import PromoFeature from '~/components/Promo/PromoFeature';

import { type VoucherUserBulkBatch, useVoucherUserBulkOperation } from '~/hooks/apis/promo/useVoucherUserBulkOperation';

import { type ListAllVoucherUsersQuery } from '~/services/PromoService';

import { isoToSydneyDate } from '~/utils/dateFormatter';

import { type SelectedVoucherUser } from './voucherUserBulkBatches';

type Props = {
  voucherExpiry?: string;
  matchingQuery: ListAllVoucherUsersQuery;
  matchingCount: number;
  matchingReady: boolean;
  hasMatchingFilters: boolean;
  selected: Array<SelectedVoucherUser>;
  batchSize: number;
  onFinished: () => Promise<void>;
  onSelectionLockChange: (locked: boolean) => void;
  onClearSelection: () => void;
};

export default function VoucherUserBulkActions({
  voucherExpiry,
  selected,
  matchingQuery,
  matchingCount,
  matchingReady,
  hasMatchingFilters,
  batchSize,
  onFinished,
  onSelectionLockChange,
  onClearSelection,
}: Props) {
  const voucherExpiryDate = voucherExpiry ? isoToSydneyDate(voucherExpiry) : '';
  const {
    action,
    changeAction,
    tagAdd,
    setTagAdd,
    tagRemove,
    setTagRemove,
    expiryDate,
    setExpiryDate,
    isExpiryValid,
    scope,
    reviewedCount,
    loadedCount,
    reviewedExpiry,
    reviewedTags,
    batches,
    isRunning,
    confirmOpen,
    error,
    completed,
    refreshError,
    eligibleCount,
    processedCount,
    skippedCount,
    isLoadingMatching,
    preview,
    execute,
    cancel,
  } = useVoucherUserBulkOperation({
    defaultExpiryDate: voucherExpiryDate,
    selected,
    matchingQuery,
    matchingCount,
    matchingReady,
    hasMatchingFilters,
    batchSize,
    onFinished,
    onSelectionLockChange,
  });
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 });

  const columns = useMemo<Array<GridColDef<VoucherUserBulkBatch>>>(
    () => [
      { field: 'id', headerName: 'Batch', width: 90 },
      { field: 'voucherId', headerName: 'Voucher ID', flex: 1, minWidth: 210 },
      {
        field: 'requested',
        headerName: 'Selected',
        width: 100,
        valueGetter: (_, row) => (action === 'expiry' ? row.voucherUserIds.length : row.userIds.length),
      },
      {
        field: 'eligible',
        headerName: 'Eligible',
        width: 100,
        valueGetter: (_, row) => (action === 'expiry' ? row.voucherUserIds.length : row.eligibleUserIds.length),
      },
      { field: 'processedCount', headerName: action === 'expiry' ? 'Updated' : 'Deleted', width: 100 },
      { field: 'skippedCount', headerName: 'Skipped', width: 100 },
      {
        field: 'status',
        headerName: 'Status',
        width: 160,
        renderCell: ({ row }) =>
          row.status === 'loading' ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={16} />
              <Typography component="span" variant="body2">
                Working
              </Typography>
            </Stack>
          ) : (
            <Typography color={row.status === 'failed' ? 'error' : 'text.primary'} title={row.error}>
              {row.status}
            </Typography>
          ),
      },
    ],
    [action],
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">Bulk actions · {selected.length} selected</Typography>
          <Button
            variant="text"
            onClick={onClearSelection}
            disabled={isRunning || confirmOpen || selected.length === 0}
          >
            Clear selection
          </Button>
        </Stack>
        <PromoFeature
          presentation="radioSelect"
          label="Bulk action"
          disabled={isRunning || confirmOpen}
          disabledReason="Finish or cancel the current bulk operation before changing actions."
          value={action}
          onChange={(nextAction) => {
            if (nextAction === 'delete' || nextAction === 'expiry') {
              changeAction(nextAction);
            }
          }}
          options={[
            {
              value: 'delete',
              label: 'Delete',
              disabled: isRunning,
              disabledReason: 'Wait until the current bulk operation has finished.',
            },
            {
              value: 'expiry',
              label: 'Update expiry',
            },
          ]}
        />
        {action === 'expiry' && (
          <>
            <Alert severity="info">
              Updates target captured assignment IDs in batches of up to 500 across vouchers. Used assignments are
              skipped.
            </Alert>
            <TextField
              label="New expiry date"
              type="date"
              value={expiryDate}
              onChange={(event) => setExpiryDate(event.target.value)}
              disabled={isRunning || confirmOpen}
              required
              InputLabelProps={{ shrink: true }}
              error={Boolean(expiryDate) && !isExpiryValid}
              helperText={
                expiryDate && !isExpiryValid
                  ? 'Choose a future expiry date.'
                  : 'Applies to each captured voucher user assignment.'
              }
            />
            <Button
              variant="text"
              onClick={() => setExpiryDate(voucherExpiryDate)}
              disabled={isRunning || confirmOpen || !voucherExpiryDate || expiryDate === voucherExpiryDate}
            >
              Set to voucher expiry
            </Button>
            <TextField
              label="Reporting tag to add"
              value={tagAdd}
              onChange={(event) => setTagAdd(event.target.value)}
              disabled={isRunning || confirmOpen}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              label="Reporting tag to remove"
              value={tagRemove}
              onChange={(event) => setTagRemove(event.target.value)}
              disabled={isRunning || confirmOpen}
              inputProps={{ maxLength: 100 }}
            />
          </>
        )}
        <Typography variant="body2" color="text.secondary">
          Select all applies to the current table page. Selected rows remain selected when you move between pages. The
          all-pages button requires a filtered search and includes every record matching the filters. Both actions
          preview eligibility and require confirmation before making changes.
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            onClick={() => void preview('selected')}
            disabled={isRunning || confirmOpen || selected.length === 0 || (action === 'expiry' && !isExpiryValid)}
          >
            {action === 'expiry' ? 'Update' : 'Delete'} {selected.length} selected records
          </Button>
          <Button
            variant="text"
            onClick={() => void preview('matching')}
            disabled={
              isRunning ||
              confirmOpen ||
              !matchingReady ||
              !hasMatchingFilters ||
              matchingCount === 0 ||
              (action === 'expiry' && !isExpiryValid)
            }
          >
            {action === 'expiry' ? 'Update' : 'Delete'} {matchingCount} matching records across all pages
          </Button>
        </Stack>
        {isLoadingMatching && (
          <Typography variant="body2">
            Loading matching records: {loadedCount} of {reviewedCount}
          </Typography>
        )}
        {batches.length > 0 && (
          <DataGrid
            rows={batches}
            columns={columns}
            autoHeight
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sx={{ backgroundColor: 'background.paper' }}
          />
        )}
        {error && <Alert severity="error">{error}</Alert>}
        {completed && !isRunning && (
          <Alert severity="info">
            {action === 'expiry'
              ? `Updated expiry for ${processedCount} voucher users. ${skippedCount} were skipped.`
              : `Deleted ${processedCount} voucher users. ${skippedCount} were skipped.`}
            {!refreshError && ' The list has been refreshed.'}
          </Alert>
        )}
        {refreshError && <Alert severity="warning">{refreshError}</Alert>}
      </Stack>
      <Dialog open={confirmOpen} onClose={isRunning ? undefined : cancel}>
        <DialogTitle>
          {action === 'expiry'
            ? 'Are you sure you want to update expiry for captured voucher users?'
            : scope === 'matching'
              ? 'Are you sure you want to delete matching voucher users across all pages?'
              : 'Are you sure you want to delete selected voucher users?'}
        </DialogTitle>
        <DialogContent>
          {action === 'expiry' ? (
            <Typography>
              Update expiry to {reviewedExpiry} for {eligibleCount} eligible assignments of {reviewedCount} captured
              records
              {scope === 'matching' ? ' matching the filters across all pages' : ' from your selection'}. {skippedCount}{' '}
              assignments were skipped or not found. Only previewed IDs will be submitted.
              {reviewedTags.add && ` Add reporting tag: ${reviewedTags.add}.`}
              {reviewedTags.remove && ` Remove reporting tag: ${reviewedTags.remove}.`}
            </Typography>
          ) : (
            <Typography>
              This will permanently delete up to {eligibleCount} of {reviewedCount}{' '}
              {scope === 'matching'
                ? 'voucher users matching the filters when preview started, across all pages'
                : 'selected voucher users'}
              . {skippedCount} are ineligible. Eligibility is checked again during deletion. Only the records captured
              for this preview will be submitted; newly matching records are excluded.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={cancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void execute()}
            disabled={eligibleCount === 0 || isRunning}
          >
            Confirm {action === 'expiry' ? 'expiry update' : 'delete'} operation
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
